import { resolveSigVars } from './signatureMergeVars';
import { SIGNATURE_BLOCK_ATTR, sanitizeSignatureHtml } from './signatureSanitize';

/**
 * editorSafeHtml — downgrade stored e-mail HTML into markup the composer's
 * TipTap schema round-trips UNCHANGED.
 *
 * ## Why this exists (issue #597 / defect N1)
 *
 * Stored signatures are email-safe TABLE markup: `routes/signatures.py`'s
 * `_build_signature_html` emits `<table cellpadding cellspacing border style>`
 * with nested `<div style>` and `<td colspan>`, and the client fallback in
 * `AdminSignaturesPage.jsx` emits the same shape.
 *
 * `RichTextEditor` cannot hold that. Its schema is StarterKit + Underline +
 * Link + Image + TextAlign + Placeholder + EmailButton — no table nodes and no
 * inline-style preservation (`@tiptap/extension-table` and `@tiptap/html` are
 * both absent from package.json). Every external update round-trips through
 * `setContent` → `onUpdate` → `getHTML()`, so the HTML that goes in is NOT the
 * HTML that comes back.
 *
 * Two consequences, both live on the contact composer before this module:
 *
 *  1. the signature reaches the recipient mangled — the table layout and every
 *     inline style are dropped on the way into the editor;
 *  2. replace-not-stack silently fails — the body holds normalised HTML while
 *     the tracked "what we last inserted" value holds the raw stored HTML, so
 *     an exact-match strip never finds it and APPENDS instead. Two signatures
 *     on the first switch.
 *
 * ## The contract is a fixed point, not merely "sanitised"
 *
 *     getHTML(setContent(toEditorSafeHtml(x))) === toEditorSafeHtml(x)
 *
 * That property — not the tag allowlist — is what makes the exact-match strip
 * reliable, and it is what `__tests__/editorSafeHtml.test.js` asserts against a
 * REAL editor. Asserting it against a mocked RichTextEditor proves nothing;
 * mocking the editor is exactly what let this defect ship green under #510.
 *
 * To hold that property the output must be the editor's own canonical
 * serialisation, which means this module mirrors several ProseMirror
 * normalisations deliberately:
 *
 *  - `<b>`/`<i>`/`<strike>`/`<del>` are re-emitted as `<strong>`/`<em>`/`<s>`
 *    (the marks' `renderHTML` forms);
 *  - a list item's text is wrapped in a paragraph (`<li><p>…</p></li>`);
 *  - runs of whitespace collapse to one space and each block is trimmed,
 *    because ProseMirror's DOM parser discards that whitespace;
 *  - `<a>` and `<img>` are re-emitted carrying the attributes the configured
 *    Link/Image extensions add, in the order they serialise them.
 *
 * The output is built as a DOM tree and read back with `innerHTML` rather than
 * concatenated as strings, so text and attribute escaping is produced by the
 * same serialiser `editor.getHTML()` uses.
 *
 * ## Do NOT reuse `sanitizeEmailHtml` (inboxUtils.js) for this
 *
 * It is DOMPurify-based and does the OPPOSITE job: it PRESERVES tables and
 * inline styles so received mail renders faithfully inside an iframe. Feeding
 * signatures through it would keep exactly the markup the editor then destroys.
 *
 * ## Signatures are NOT flattened any more (#970)
 *
 * A signature goes into the composer as one locked `signatureBlock` node
 * (`components/SignatureBlockExtension.js`) that holds the builder's sanitised
 * HTML and renders it as-is, so its table layout and inline styles reach the
 * recipient. `buildSignatureHtml` builds that block; `replaceSignatureBlock`
 * swaps or removes it by its wrapper (`div[data-ncrm-signature]`), never by
 * string match. `toEditorSafeHtml` still flattens everything else (templates,
 * pasted content) and passes a block through intact, re-sanitised.
 */

// Inline elements we keep, mapped to the tag the corresponding TipTap mark
// actually renders. `<b>` parses as bold but serialises as `<strong>`, so
// emitting `<b>` here would break the fixed point on the first round-trip.
const INLINE_TAG_MAP = {
    STRONG: 'strong',
    B: 'strong',
    EM: 'em',
    I: 'em',
    U: 'u',
    INS: 'u',
    S: 's',
    STRIKE: 's',
    DEL: 's',
    CODE: 'code',
};

// Block elements we keep as-is (the schema has a node for each).
const BLOCK_TAGS = new Set(['P', 'H1', 'H2', 'H3', 'BLOCKQUOTE', 'UL', 'OL']);

// Elements that carry block structure we must descend into rather than treat
// as inline — tables become stacked paragraphs, one per row.
const TABLE_CONTAINER_TAGS = new Set(['TABLE', 'TBODY', 'THEAD', 'TFOOT', 'COLGROUP']);

// Never rendered, and never contribute text.
const DROP_TAGS = new Set(['SCRIPT', 'STYLE', 'HEAD', 'META', 'LINK', 'TITLE', 'COL', 'NOSCRIPT']);

// Link/Image attributes. These mirror `RichTextEditor`'s extension config —
// `Link.configure({ openOnClick: false, HTMLAttributes: { class } })` and
// `Image.configure({ HTMLAttributes: { class } })` — because the extensions
// inject them on serialisation whether or not the source markup had them.
// They are listed in serialisation order (links: target, rel, class, href;
// images: class, src, alt); see the fixed-point test, which is what proves
// this list and its order are still correct.
const LINK_CLASS = 'text-stone-700 underline hover:text-stone-800';
const LINK_TARGET = '_blank';
const LINK_REL = 'noopener noreferrer nofollow';
const IMAGE_CLASS = 'max-w-full h-auto rounded-lg my-4';

const SAFE_URL = /^(https?:|mailto:|tel:)/i;

/**
 * Marks ProseMirror would infer from an element's inline `style`.
 *
 * StarterKit's Bold/Italic/Strike and the Underline extension all declare
 * STYLE parse rules alongside their tag rules — `font-weight:700` becomes
 * `<strong>`, `font-style:italic` becomes `<em>`, and a `text-decoration`
 * containing `underline` / `line-through` becomes `<u>` / `<s>`.
 *
 * `_build_signature_html` leans on exactly this: a signature's NAME is a
 * `<span style="font-weight:700">`, never a `<strong>`. Dropping the span
 * without reading its style would silently un-bold every signature's name —
 * the one piece of formatting a signature always has — and would make this
 * downgrade LOSE something the raw (mangled) path actually kept.
 *
 * Returned innermost-last so the wrapping order is stable, which the fixed
 * point needs.
 */
function marksFromStyle(el) {
    const style = (el.getAttribute && el.getAttribute('style')) || '';
    if (!style) return [];
    const lower = style.toLowerCase();
    const marks = [];
    const weight = /font-weight\s*:\s*([^;]+)/.exec(lower);
    if (weight && /^\s*(bold(er)?|[5-9]\d{2,})\s*$/.test(weight[1])) marks.push('strong');
    if (/font-style\s*:\s*italic/.test(lower)) marks.push('em');
    const decoration = /text-decoration[^:]*:\s*([^;]+)/.exec(lower);
    if (decoration && decoration[1].includes('underline')) marks.push('u');
    if (decoration && decoration[1].includes('line-through')) marks.push('s');
    return marks;
}

function wrapInMarks(target, marks, doc) {
    let host = target;
    for (const mark of marks) {
        const el = doc.createElement(mark);
        host.appendChild(el);
        host = el;
    }
    return host;
}

function collapseWhitespace(text) {
    // ProseMirror's DOM parser collapses runs of whitespace (including the
    // newlines `_build_signature_html` indents its table with) into a single
    // space. Mirroring that here is required for the fixed point, not cosmetic.
    return String(text).replace(/[\s ]+/g, ' ');
}

function isSafeUrl(url) {
    const value = String(url || '').trim();
    if (!value) return false;
    // Anything not explicitly http(s)/mailto/tel — `javascript:`, `data:`,
    // `vbscript:` — is dropped rather than carried into a message body.
    return SAFE_URL.test(value);
}

/**
 * Append the inline content of `source` into `target`, emitting only the
 * marks/nodes the schema can hold.
 *
 * @param {Node} source   node whose children are read
 * @param {Element} target element the canonical inline nodes are appended to
 * @param {Document} doc
 */
function appendInline(source, target, doc) {
    for (const node of Array.from(source.childNodes)) {
        if (node.nodeType === 3) {
            const text = collapseWhitespace(node.nodeValue);
            if (text) target.appendChild(doc.createTextNode(text));
            continue;
        }
        if (node.nodeType !== 1) continue;

        const tag = node.tagName.toUpperCase();
        if (DROP_TAGS.has(tag)) continue;

        if (tag === 'BR') {
            target.appendChild(doc.createElement('br'));
            continue;
        }

        // <img> is a BLOCK node in this schema, not an inline one: TipTap hoists
        // it out of any paragraph it is written into. collectBlocks emits it as a
        // top-level sibling; inline position is unrepresentable, so drop it here.
        if (tag === 'IMG') continue;

        if (tag === 'A') {
            const href = node.getAttribute('href');
            if (!isSafeUrl(href)) {
                // Keep the anchor's text, drop the unusable link.
                appendInline(node, target, doc);
                continue;
            }
            const a = doc.createElement('a');
            // Attribute ORDER is part of the fixed point: innerHTML serialises in
            // insertion order, and the Link extension's renderHTML merges its
            // configured HTMLAttributes BEFORE the mark's own href. Emitting href
            // first round-trips to a different string and the strip stops matching.
            a.setAttribute('target', LINK_TARGET);
            a.setAttribute('rel', LINK_REL);
            a.setAttribute('class', LINK_CLASS);
            a.setAttribute('href', href);
            appendInline(node, a, doc);
            target.appendChild(a);
            continue;
        }

        const mapped = INLINE_TAG_MAP[tag];
        if (mapped) {
            const el = doc.createElement(mapped);
            appendInline(node, el, doc);
            // An empty mark has nothing to mark and ProseMirror drops it.
            if (el.childNodes.length) target.appendChild(el);
            continue;
        }

        // Everything else — <span>, <font>, <div> used inline, unknown tags — is
        // unwrapped: its children are kept, the element itself is not. Any mark
        // its inline style implied is re-emitted as a real tag first, so the
        // formatting survives the loss of the style attribute.
        const styleMarks = marksFromStyle(node);
        if (styleMarks.length) {
            const holder = doc.createElement('div');
            const inner = wrapInMarks(holder, styleMarks, doc);
            appendInline(node, inner, doc);
            if (inner.childNodes.length) moveChildren(holder, target);
            continue;
        }
        appendInline(node, target, doc);
    }
}

function inlineHtmlOf(source, doc) {
    const holder = doc.createElement('div');
    appendInline(source, holder, doc);
    return holder;
}

function hasBlockDescendant(el) {
    for (const child of Array.from(el.children || [])) {
        const tag = child.tagName.toUpperCase();
        if (BLOCK_TAGS.has(tag) || TABLE_CONTAINER_TAGS.has(tag) || tag === 'TR' || tag === 'DIV' || tag === 'IMG') {
            return true;
        }
        if (hasBlockDescendant(child)) return true;
    }
    return false;
}

function trimInlineEdges(el) {
    // ProseMirror trims the leading/trailing whitespace of a textblock.
    const first = el.firstChild;
    if (first && first.nodeType === 3) {
        first.nodeValue = first.nodeValue.replace(/^ +/, '');
        if (!first.nodeValue) el.removeChild(first);
    }
    const last = el.lastChild;
    if (last && last.nodeType === 3) {
        last.nodeValue = last.nodeValue.replace(/ +$/, '');
        if (!last.nodeValue) el.removeChild(last);
    }
    return el;
}

function moveChildren(from, to) {
    while (from.firstChild) to.appendChild(from.firstChild);
    return to;
}

/**
 * Walk `source`'s children, appending canonical BLOCK elements to `out`.
 * Inline content encountered between blocks is buffered and flushed as a
 * paragraph, which is how a bare text node or a `<td>`'s contents becomes one.
 */
function collectBlocks(source, out, doc) {
    let pending = doc.createElement('div');

    const flush = () => {
        trimInlineEdges(pending);
        if (pending.childNodes.length) {
            out.appendChild(moveChildren(pending, doc.createElement('p')));
        }
        pending = doc.createElement('div');
    };

    for (const node of Array.from(source.childNodes)) {
        if (node.nodeType === 3) {
            const text = collapseWhitespace(node.nodeValue);
            if (text.trim()) pending.appendChild(doc.createTextNode(text));
            continue;
        }
        if (node.nodeType !== 1) continue;

        const tag = node.tagName.toUpperCase();
        if (DROP_TAGS.has(tag)) continue;

        // #970 — a signature block passes through intact (re-sanitised), so a
        // pasted or re-parsed signature keeps its layout instead of flattening.
        if (node.hasAttribute(SIGNATURE_BLOCK_ATTR)) {
            flush();
            const block = signatureBlockElement(doc, node.getAttribute(SIGNATURE_BLOCK_ATTR), node.innerHTML);
            if (block) out.appendChild(block);
            continue;
        }

        if (tag === 'P' || tag === 'H1' || tag === 'H2' || tag === 'H3') {
            flush();
            const el = doc.createElement(tag.toLowerCase());
            appendInline(node, el, doc);
            trimInlineEdges(el);
            // An empty <p> is legal and meaningful (spacing); an empty heading
            // is not — the schema would render a stray blank heading.
            if (tag === 'P' || el.childNodes.length) out.appendChild(el);
            continue;
        }

        if (tag === 'BLOCKQUOTE') {
            flush();
            const quote = doc.createElement('blockquote');
            collectBlocks(node, quote, doc);
            if (!quote.childNodes.length) quote.appendChild(doc.createElement('p'));
            out.appendChild(quote);
            continue;
        }

        if (tag === 'UL' || tag === 'OL') {
            flush();
            const list = doc.createElement(tag.toLowerCase());
            for (const li of Array.from(node.children || [])) {
                if (li.tagName.toUpperCase() !== 'LI') continue;
                const item = doc.createElement('li');
                if (hasBlockDescendant(li)) {
                    collectBlocks(li, item, doc);
                } else {
                    const para = doc.createElement('p');
                    appendInline(li, para, doc);
                    trimInlineEdges(para);
                    item.appendChild(para);
                }
                // listItem's content is `paragraph block*` — it must not be empty.
                if (!item.childNodes.length) item.appendChild(doc.createElement('p'));
                list.appendChild(item);
            }
            if (list.childNodes.length) out.appendChild(list);
            continue;
        }

        if (tag === 'HR') {
            flush();
            out.appendChild(doc.createElement('hr'));
            continue;
        }

        if (tag === 'IMG') {
            const src = node.getAttribute('src');
            // A signature logo may legitimately be a data: image; anything that is
            // neither that nor an http(s) URL is dropped rather than carried into
            // a message body.
            if (!isSafeUrl(src) && !/^data:image\//i.test(String(src || ''))) continue;
            flush();
            const img = doc.createElement('img');
            // class BEFORE src BEFORE alt — the Image extension's serialisation
            // order. See the note on link attribute order above.
            img.setAttribute('class', IMAGE_CLASS);
            img.setAttribute('src', src);
            const alt = node.getAttribute('alt');
            if (alt) img.setAttribute('alt', alt);
            out.appendChild(img);
            continue;
        }

        if (TABLE_CONTAINER_TAGS.has(tag)) {
            flush();
            collectBlocks(node, out, doc);
            continue;
        }

        if (tag === 'TR') {
            // One row becomes one paragraph: the cells' inline content joined
            // by a space. A signature table's rows are visual lines, so this
            // is the stacked-lines form the recipient already sees today —
            // only now it is deterministic.
            flush();
            const row = doc.createElement('p');
            let wroteCell = false;
            for (const cell of Array.from(node.children || [])) {
                const cellTag = cell.tagName.toUpperCase();
                if (cellTag !== 'TD' && cellTag !== 'TH') continue;
                if (hasBlockDescendant(cell)) {
                    // A cell carrying real block structure (a nested table, a
                    // list) cannot collapse into this row's paragraph without
                    // losing it — emit the row so far, then the cell's blocks.
                    trimInlineEdges(row);
                    if (row.childNodes.length) out.appendChild(moveChildren(row, doc.createElement('p')));
                    while (row.firstChild) row.removeChild(row.firstChild);
                    wroteCell = false;
                    collectBlocks(cell, out, doc);
                    continue;
                }
                const holder = inlineHtmlOf(cell, doc);
                trimInlineEdges(holder);
                if (!holder.childNodes.length) continue;
                if (wroteCell) row.appendChild(doc.createTextNode(' '));
                moveChildren(holder, row);
                wroteCell = true;
            }
            trimInlineEdges(row);
            if (row.childNodes.length) out.appendChild(row);
            continue;
        }

        if (tag === 'TD' || tag === 'TH') {
            // Reached without an enclosing <tr> (malformed markup).
            flush();
            collectBlocks(node, out, doc);
            continue;
        }

        if (tag === 'DIV' || tag === 'SECTION' || tag === 'ARTICLE' || tag === 'BODY' || tag === 'CENTER') {
            flush();
            if (hasBlockDescendant(node)) {
                collectBlocks(node, out, doc);
            } else {
                const para = doc.createElement('p');
                appendInline(node, para, doc);
                trimInlineEdges(para);
                if (para.childNodes.length) out.appendChild(para);
            }
            continue;
        }

        // Inline (or unknown) — buffer it into the pending paragraph.
        const holder = inlineHtmlOf(
            // appendInline reads CHILDREN, so wrap the node to have it handled
            // as one item rather than descended into.
            (() => { const w = doc.createElement('div'); w.appendChild(node.cloneNode(true)); return w; })(),
            doc,
        );
        moveChildren(holder, pending);
    }

    flush();
}

/**
 * Convert stored e-mail HTML into markup the composer's editor round-trips
 * unchanged.
 *
 * @param {string} html stored signature/template HTML (table markup, inline
 *   styles, `<div>` wrappers — whatever `_build_signature_html` produced)
 * @returns {string} canonical editor-safe HTML. `''` for empty input.
 */
export function toEditorSafeHtml(html) {
    if (!html) return '';
    if (typeof DOMParser === 'undefined') {
        // No DOM (a non-jsdom test env, SSR). Returning the input unchanged
        // would hand the caller markup the strip cannot match, so return
        // nothing rather than something that silently misbehaves.
        return '';
    }
    const parsed = new DOMParser().parseFromString(String(html), 'text/html');
    const doc = parsed;
    const out = doc.createElement('div');
    collectBlocks(doc.body, out, doc);
    return out.innerHTML;
}

/** A `div[data-ncrm-signature]` holding the sanitised HTML, or null if nothing is left. */
function signatureBlockElement(doc, id, html) {
    const inner = sanitizeSignatureHtml(html);
    if (!inner) return null;
    const el = doc.createElement('div');
    el.setAttribute(SIGNATURE_BLOCK_ATTR, id || '');
    el.innerHTML = inner;
    return el;
}

/**
 * The one recipe for turning a stored signature into the HTML that goes in the
 * body — used by `TemplateSignatureSelectors`, `ContactEmailComposer`, the
 * Sequence Editor and every caller that seeds a signature when a composer opens.
 *
 * #970: the result is a signature BLOCK (see the header): the stored layout,
 * merge-resolved and sanitised, inside `div[data-ncrm-signature="<id>"]`.
 *
 * Order matters: resolve merge variables FIRST, then sanitise, so a
 * `{{Token}}` is never split or dropped before it is resolved.
 *
 * @param {{content?: string, signature_id?: string}} signature stored signature record
 * @param {object} user the signed-in user, for `{{Sender*}}` resolution
 * @param {string} senderEmail the mailbox actually sending. Empty string when
 *   unresolved — NEVER the CRM login email (#487).
 * @returns {string} the signature block's HTML, or '' when there is nothing to insert
 */
export function buildSignatureHtml(signature, user, senderEmail) {
    if (!signature?.content || typeof DOMParser === 'undefined') return '';
    const doc = new DOMParser().parseFromString('', 'text/html');
    const el = signatureBlockElement(doc, signature.signature_id, resolveSigVars(signature.content, user, senderEmail));
    return el ? el.outerHTML : '';
}

/**
 * Replace-not-stack, by block (#970): put `blockHtml` where the body's
 * signature block is, or remove the block when `blockHtml` is empty (None).
 * Only the first block is touched: any later one is content the user pasted
 * and must not disappear on a switch (forwarded mail has its markers removed
 * by `withoutSignatureBlocks` before it reaches the body).
 *
 * Pure: safe inside a React state updater, which StrictMode runs twice.
 *
 * @param {string} body current body HTML
 * @param {string} blockHtml output of `buildSignatureHtml`, or '' to remove
 * @param {{appendIfMissing?: boolean, legacyHtml?: string}} [opts]
 *   `appendIfMissing`: append when the body has no block (default true);
 *   false re-resolves in place only.
 *   `legacyHtml`: the flattened signature a caller inserted before #970 (a
 *   restored draft). When the body has no block but holds this exact string,
 *   it is replaced like a block, so an old draft does not stack.
 * @returns {string} the new body
 */
export function replaceSignatureBlock(body, blockHtml, { appendIfMissing = true, legacyHtml = '' } = {}) {
    let current = body || '';
    if (typeof DOMParser === 'undefined') return current;
    const hasBlock = current.includes(SIGNATURE_BLOCK_ATTR);
    if (!hasBlock && legacyHtml && !legacyHtml.includes(SIGNATURE_BLOCK_ATTR) && current.includes(legacyHtml)) {
        // A function replacer: a signature may contain `$&` and friends.
        return current.replace(legacyHtml, () => blockHtml || '');
    }
    if (!hasBlock && (!blockHtml || !appendIfMissing)) return current;
    if (!hasBlock) {
        const text = current.replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').trim();
        return `${current}${text ? '<p></p>' : ''}${blockHtml}`;
    }
    const doc = new DOMParser().parseFromString(current, 'text/html');
    const blocks = Array.from(doc.body.querySelectorAll(`[${SIGNATURE_BLOCK_ATTR}]`));
    const [first] = blocks;
    if (!first) return blockHtml && appendIfMissing ? `${current}<p></p>${blockHtml}` : current;
    if (blockHtml) {
        const holder = doc.createElement('div');
        holder.innerHTML = blockHtml;
        const next = holder.firstElementChild;
        if (next) first.replaceWith(next); else first.remove();
    } else {
        first.remove();
    }
    return doc.body.innerHTML;
}

/**
 * Forwarded or quoted HTML with every signature-block marker removed, so a
 * signature inside it flattens like any other quoted content instead of
 * becoming a block that replace-not-stack would treat as the sender's own.
 *
 * @param {string} html forwarded/quoted HTML
 * @returns {string} the same HTML without `data-ncrm-signature` attributes
 */
export function withoutSignatureBlocks(html) {
    if (!html || typeof html !== 'string') return html;
    return html.replace(/\sdata-ncrm-signature(?:="[^"]*"|='[^']*')?/gi, '');
}

/**
 * The same HTML with every signature block's id blanked, marker kept: for a
 * body that is stored and sent later (a sequence step), so re-editing it still
 * finds the block while the internal id never reaches a recipient.
 *
 * @param {string} html stored body HTML
 * @returns {string}
 */
export function withoutSignatureIds(html) {
    if (!html || typeof html !== 'string') return html;
    return html.replace(/(\sdata-ncrm-signature)=(?:"[^"]*"|'[^']*')/gi, '$1=""');
}

/**
 * The id of the signature block in a body: `''` for a block without an id,
 * `null` when the body holds no block. Lets a picker show what the body
 * really holds (e.g. when a composer reopens over a kept draft).
 *
 * @param {string} body current body HTML
 * @returns {string|null}
 */
export function signatureBlockId(body) {
    if (!body || !body.includes(SIGNATURE_BLOCK_ATTR) || typeof DOMParser === 'undefined') return null;
    const doc = new DOMParser().parseFromString(body, 'text/html');
    const el = doc.body.querySelector(`[${SIGNATURE_BLOCK_ATTR}]`);
    return el ? el.getAttribute(SIGNATURE_BLOCK_ATTR) || '' : null;
}

/**
 * Did the user actually write anything, ignoring the signature block?
 *
 * Every composer opens with a signature, so a bare `!body.trim()` check can
 * never fail and a signature-only message would go out on a stray click. Every
 * window disables Send on this (#970), so they cannot drift on what "empty"
 * means. Forwarded or quoted text counts as content.
 *
 * @param {string} body current editor HTML
 * @returns {boolean} true when there is real authored content
 */
export function hasAuthoredText(body) {
    let withoutSignature = body || '';
    if (withoutSignature.includes(SIGNATURE_BLOCK_ATTR) && typeof DOMParser !== 'undefined') {
        const doc = new DOMParser().parseFromString(withoutSignature, 'text/html');
        doc.body.querySelectorAll(`[${SIGNATURE_BLOCK_ATTR}]`).forEach(n => n.remove());
        withoutSignature = doc.body.innerHTML;
    }
    if (!withoutSignature) return false;
    // Text content only: an empty body is a run of <p></p>/<br>, which is what
    // the seed's leading spacer paragraphs leave behind after the strip.
    const text = withoutSignature
        .replace(/<[^>]*>/g, ' ')
        .replace(/&nbsp;/gi, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    return text.length > 0;
}
